import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { apiError, useImagingApi } from './api';

interface Clip {
  id: string;
  status: string;
  in_viewer: boolean;
  error?: string;
}

/** A compact archive status; all clinical playback happens in the OHIF study viewer. */
export function CineViewer({ studyId, canRetry = true }: { studyId: string; canRetry?: boolean }) {
  const { org, base, userId } = useImagingApi();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = ['imaging', org, userId, studyId, 'clips'];
  const clips = useQuery({
    queryKey: key,
    queryFn: async () => (await clientApi.get<Clip[]>(`${base}/studies/${studyId}/clips`)).data,
    refetchInterval: (query) =>
      query.state.data?.some((c) => ['processing', 'queued'].includes(c.status)) ? 4000 : false,
  });
  if (!clips.data?.length && !clips.error) return null;
  const ready = clips.data?.filter((c) => c.in_viewer).length || 0;
  const pending = clips.data?.filter((c) => ['processing', 'queued'].includes(c.status)).length || 0;
  const retry = clips.data?.filter((c) => !c.in_viewer && !['processing', 'queued'].includes(c.status)) || [];
  async function addToViewer() {
    setBusy(true);
    setError('');
    try {
      for (const clip of retry) {
        const { data } = await clientApi.post(`${base}/studies/${studyId}/clips/${clip.id}/retry`);
        if (!data.scheduled) throw new Error('Cine could not be scheduled; try again from Imaging intake.');
      }
      await qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div aria-label="Cine archive status" className="flex flex-wrap items-center gap-2 text-sm">
      <span>{ready} cine {ready === 1 ? 'clip' : 'clips'} in OHIF{pending ? ` · ${pending} processing` : ''}</span>
      {retry.length > 0 && canRetry && (
        <Button variant="outline" size="sm" disabled={busy} onClick={() => void addToViewer()}>
          {busy ? 'Adding clips…' : `Add ${retry.length} ${retry.length === 1 ? 'clip' : 'clips'} to OHIF`}
        </Button>
      )}
      {retry.length > 0 && !canRetry && <span>{retry.length} awaiting imaging intake</span>}
      {(error || clips.error || retry.some((c) => c.error)) && (
        <span role="alert">{error || (clips.error ? apiError(clips.error) : retry.find((c) => c.error)?.error)}</span>
      )}
    </div>
  );
}
